import { Component } from '@theme/component'
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events'
import { morph } from '@theme/morph'
import { requestYieldCallback } from '@theme/utilities'

/**
 * Custom variant picker for products with more than 3 options using metafields
 * Handles complex product configurations like fabric, fit, inseam, and color
 */
export default class CustomVariantPicker extends Component {
  /** @type {Object} */
  #variantOptions = {}

  /** @type {Object} */
  #variantMatrix = {}

  /** @type {Object} */
  #currentSelection = {}

  /** @type {Object} */
  #availableOptions = {}

  /** @type {Array} */
  #existingVariants = []

  /** @type {Array} */
  #existingOptions = []

  /** @type {string | undefined} */
  #pendingRequestUrl

  /** @type {AbortController | undefined} */
  #abortController

  connectedCallback() {
    super.connectedCallback()
    
    // Debug: Always log when component loads
    console.log('🚀 CUSTOM VARIANT PICKER LOADED!', this)
    console.log('🔍 CUSTOM VARIANT PICKER - Element:', this)
    console.log('🔍 CUSTOM VARIANT PICKER - Dataset:', this.dataset)

    // Parse metafield data
    this.#parseVariantData()
    
    // Initialize available options based on current selection
    this.#updateAvailableOptions()
    
    // Render the variant options HTML
    this.#renderVariantOptions()
    
    // Set up event listeners
    this.addEventListener('change', this.#handleVariantChange.bind(this))
    this.addEventListener('input', this.#handleVariantChange.bind(this))
  }

  /**
   * Parses variant options and matrix from metafields, metaobjects, and existing variants
   */
  #parseVariantData() {
    try {
      // Debug: Log all dataset values
      console.log('🔍 CUSTOM VARIANT PICKER DEBUG - Dataset Values:', {
        variantOptions: this.dataset.variantOptions,
        variantMatrix: this.dataset.variantMatrix,
        existingVariants: this.dataset.existingVariants,
        existingOptions: this.dataset.existingOptions,
        variantConfig: this.dataset.variantConfig
      })
      
      // Parse hybrid variant options (combines custom + existing variants)
      this.#variantOptions = JSON.parse(this.dataset.variantOptions || '{}')
      this.#variantMatrix = JSON.parse(this.dataset.variantMatrix || '{}')
      
      // Parse existing variants for inventory and pricing
      this.#existingVariants = JSON.parse(this.dataset.existingVariants || '[]')
      this.#existingOptions = JSON.parse(this.dataset.existingOptions || '[]')
      
      // Try to parse from metaobject for additional configuration
      const variantConfig = this.dataset.variantConfig
      if (variantConfig) {
        const config = JSON.parse(variantConfig)
        // Merge metaobject data with existing data
        this.#variantOptions = {
          ...this.#variantOptions,
          fabrics: config.fabrics || this.#variantOptions.fabrics || [],
          fits: config.fits || this.#variantOptions.fits || [],
          inseams: config.inseams || this.#variantOptions.inseams || [],
          colors: config.colors || this.#variantOptions.colors || []
        }
        this.#variantMatrix = config.variant_matrix || this.#variantMatrix
      }
      
      // Debug: Log parsed data
      console.log('✅ CUSTOM VARIANT PICKER DEBUG - Parsed Data:', {
        variantOptions: this.#variantOptions,
        variantMatrix: this.#variantMatrix,
        existingVariants: this.#existingVariants,
        existingOptions: this.#existingOptions
      })
    } catch (error) {
      console.error('Error parsing variant data:', error)
    }
  }

  /**
   * Handles variant change events
   * @param {Event} event - The change event
   */
  #handleVariantChange(event) {
    if (!(event.target instanceof HTMLElement)) return

    const optionType = event.target.dataset.optionType
    const optionValue = event.target.value

    if (!optionType || !optionValue) return

    // Update current selection
    this.#currentSelection[optionType] = optionValue

    // Handle fit selection - redirect to appropriate product
    if (optionType === 'fit') {
      this.#handleFitSelection(optionValue)
      return
    }

    // Update available options based on new selection
    this.#updateAvailableOptions()

    // Update form properties for cart submission
    this.#updateFormProperties()

    // Check if current combination is valid
    const isValidCombination = this.#isValidCombination()

    // Update UI state
    this.#updateUIState(isValidCombination)

    // Dispatch events
    this.#dispatchVariantEvents(isValidCombination)
  }

  /**
   * Handles fit selection and redirects to appropriate product
   * @param {string} fit - The selected fit
   */
  #handleFitSelection(fit) {
    const skuMapping = this.#variantOptions.sku_mapping
    
    if (!skuMapping || !skuMapping[fit]) {
      console.warn('No SKU mapping found for fit:', fit)
      return
    }
    
    const fitConfig = skuMapping[fit]
    const productId = fitConfig.product_id
    
    // Redirect to the appropriate product page
    const productUrl = `/products/${productId}`
    console.log('Redirecting to product:', productUrl)
    
    // You can either redirect immediately or show a message
    // window.location.href = productUrl
    
    // For now, let's show a message
    this.#showFitSelectionMessage(fit, productUrl)
  }

  /**
   * Shows a message about fit selection
   * @param {string} fit - The selected fit
   * @param {string} productUrl - The product URL
   */
  #showFitSelectionMessage(fit, productUrl) {
    const message = document.createElement('div')
    message.className = 'custom-variant-picker__fit-message'
    message.innerHTML = `
      <p>You selected <strong>${fit}</strong> fit.</p>
      <p>This will redirect you to the ${fit} fit product page.</p>
      <a href="${productUrl}" class="btn btn-primary">Go to ${fit} Fit Product</a>
    `
    
    // Clear existing content and show message
    this.innerHTML = ''
    this.appendChild(message)
  }

  /**
   * Renders the variant options HTML
   */
  #renderVariantOptions() {
    console.log('🎨 RENDERING VARIANT OPTIONS:', this.#variantOptions)
    
    // Clear existing content
    this.innerHTML = ''
    
    // Create container for variant options
    const container = document.createElement('div')
    container.className = 'custom-variant-picker__options'
    
    // Render each option type
    Object.entries(this.#variantOptions).forEach(([optionType, options]) => {
      if (options && options.length > 0) {
        const optionGroup = this.#createOptionGroup(optionType, options)
        container.appendChild(optionGroup)
      }
    })
    
    // Add stock status
    const stockStatus = document.createElement('div')
    stockStatus.className = 'custom-variant-picker__stock-status unavailable'
    stockStatus.textContent = 'Select all options'
    container.appendChild(stockStatus)
    
    this.appendChild(container)
    
    console.log('✅ VARIANT OPTIONS RENDERED!')
  }

  /**
   * Creates an option group for a specific variant type
   * @param {string} optionType - The type of option (fits, fabrics, inseams, colors)
   * @param {Array} options - Array of option values
   * @returns {HTMLElement} The option group element
   */
  #createOptionGroup(optionType, options) {
    const group = document.createElement('div')
    group.className = `custom-variant-picker__option-group custom-variant-picker__option-group--${optionType}`
    
    const label = document.createElement('label')
    label.className = 'custom-variant-picker__option-label'
    label.textContent = this.#formatOptionLabel(optionType)
    group.appendChild(label)
    
    const optionsContainer = document.createElement('div')
    optionsContainer.className = 'custom-variant-picker__options-container'
    
    options.forEach((option, index) => {
      const optionElement = this.#createOptionElement(optionType, option, index)
      optionsContainer.appendChild(optionElement)
    })
    
    group.appendChild(optionsContainer)
    return group
  }

  /**
   * Creates an individual option element
   * @param {string} optionType - The type of option
   * @param {string} optionValue - The option value
   * @param {number} index - The option index
   * @returns {HTMLElement} The option element
   */
  #createOptionElement(optionType, optionValue, index) {
    const optionId = `${optionType}-${index}`
    
    const label = document.createElement('label')
    label.className = 'custom-variant-picker__option'
    label.setAttribute('for', optionId)
    
    const input = document.createElement('input')
    input.type = 'radio'
    input.name = optionType
    input.id = optionId
    input.value = optionValue
    input.dataset.optionType = optionType
    input.className = 'custom-variant-picker__option-input'
    
    const span = document.createElement('span')
    span.className = 'custom-variant-picker__option-text'
    span.textContent = optionValue
    
    label.appendChild(input)
    label.appendChild(span)
    
    return label
  }

  /**
   * Formats option labels for display
   * @param {string} optionType - The option type
   * @returns {string} The formatted label
   */
  #formatOptionLabel(optionType) {
    const labels = {
      fits: 'Fit',
      fabrics: 'Fabric',
      colors: 'Color',
      sizes: 'Size',
      inseams: 'Inseam'
    }
    return labels[optionType] || optionType.charAt(0).toUpperCase() + optionType.slice(1)
  }

  /**
   * Updates available options based on current selection
   */
  #updateAvailableOptions() {
    const { fabric, fit, inseam } = this.#currentSelection

    // Reset available options
    this.#availableOptions = {
      fabrics: this.#variantOptions.fabrics || [],
      fits: this.#variantOptions.fits || [],
      colors: this.#variantOptions.colors || [],
      sizes: this.#variantOptions.sizes || [],
      inseams: this.#variantOptions.inseams || []
    }

    // Filter options based on selection
    if (fabric && this.#variantMatrix[fabric]) {
      if (fit && this.#variantMatrix[fabric][fit]) {
        if (inseam && this.#variantMatrix[fabric][fit][inseam]) {
          this.#availableOptions.colors = this.#variantMatrix[fabric][fit][inseam]
        } else {
          // Get all available colors for current fabric/fit combination
          const availableColors = []
          Object.values(this.#variantMatrix[fabric][fit] || {}).forEach(colors => {
            availableColors.push(...colors)
          })
          this.#availableOptions.colors = [...new Set(availableColors)]
        }
      } else {
        // Get all available fits for current fabric
        this.#availableOptions.fits = Object.keys(this.#variantMatrix[fabric] || {})
      }
    }

    // Handle conditional inseam availability for Sharp Fit
    this.#updateInseamAvailability()
    
    // Update option availability in UI
    this.#updateOptionAvailability()
  }

  /**
   * Updates inseam availability based on fit selection
   */
  #updateInseamAvailability() {
    const { fit } = this.#currentSelection
    const skuMapping = this.#variantOptions.sku_mapping
    
    if (!skuMapping || !skuMapping[fit]) {
      return
    }
    
    const fitConfig = skuMapping[fit]
    
    // For Sharp Fit, only show 32" inseam option (30" is default, no variant needed)
    if (fitConfig.has_inseam === "conditional" && fitConfig.inseam_32_only) {
      this.#availableOptions.inseams = ['32"']
    }
  }

  /**
   * Updates option availability in the UI
   */
  #updateOptionAvailability() {
    const optionTypes = ['fabric', 'fit', 'color', 'size', 'inseam']
    
    optionTypes.forEach(optionType => {
      const availableValues = this.#availableOptions[`${optionType}s`] || []
      const inputs = this.querySelectorAll(`input[name="${optionType}"]`)
      
      inputs.forEach(input => {
        const isAvailable = availableValues.includes(input.value)
        const label = input.closest('label')
        
        if (label) {
          label.classList.toggle('unavailable', !isAvailable)
          input.disabled = !isAvailable
        }
      })
    })
  }

  /**
   * Checks if current selection forms a valid combination
   * @returns {boolean}
   */
  #isValidCombination() {
    const { fabric, fit, inseam, color } = this.#currentSelection
    
    if (!fabric || !this.#variantMatrix[fabric]) return false
    if (!fit || !this.#variantMatrix[fabric][fit]) return false
    if (!inseam || !this.#variantMatrix[fabric][fit][inseam]) return false
    if (!color || !this.#variantMatrix[fabric][fit][inseam].includes(color)) return false
    
    return true
  }

  /**
   * Updates form properties for cart submission
   */
  #updateFormProperties() {
    const form = this.querySelector('form')
    if (!form) return

    // Update hidden inputs
    Object.entries(this.#currentSelection).forEach(([key, value]) => {
      const input = form.querySelector(`input[name="properties[${key}]"]`)
      if (input) {
        input.value = value
      }
    })
  }

  /**
   * Updates UI state based on selection validity
   * @param {boolean} isValid - Whether current selection is valid
   */
  #updateUIState(isValid) {
    const infoElement = this.querySelector('.custom-variant-picker__info')
    const stockStatus = this.querySelector('.custom-variant-picker__stock-status')
    
    if (stockStatus) {
      if (isValid) {
        stockStatus.textContent = 'Available'
        stockStatus.className = 'custom-variant-picker__stock-status available'
      } else {
        stockStatus.textContent = 'Select all options'
        stockStatus.className = 'custom-variant-picker__stock-status unavailable'
      }
    }
  }

  /**
   * Dispatches variant events
   * @param {boolean} isValid - Whether current selection is valid
   */
  #dispatchVariantEvents(isValid) {
    if (isValid) {
      // Create a mock variant object for compatibility
      const mockVariant = {
        id: this.#generateVariantId(),
        available: true,
        price: this.#calculatePrice(),
        compare_at_price: null,
        featured_media: this.#getFeaturedMedia()
      }

      this.dispatchEvent(new VariantSelectedEvent({ id: mockVariant.id }))
      this.dispatchEvent(new VariantUpdateEvent(mockVariant, mockVariant.id, {
        html: this,
        productId: this.dataset.productId,
        newProduct: null
      }))
    }
  }

  /**
   * Generates a unique variant ID based on current selection
   * @returns {string}
   */
  #generateVariantId() {
    const { fabric, fit, inseam, color, size } = this.#currentSelection
    
    // Try to find matching existing variant first
    const matchingVariant = this.#findMatchingVariant()
    if (matchingVariant) {
      return matchingVariant.id
    }
    
    // Generate SKU based on selection
    const sku = this.#generateSKU()
    if (sku) {
      return sku
    }
    
    // Fallback to generated ID
    return `${this.dataset.productId}-${fabric}-${fit}-${inseam}-${color}-${size}`.toLowerCase().replace(/\s+/g, '-')
  }

  /**
   * Generates SKU based on current selection and SKU mapping
   * @returns {string|null}
   */
  #generateSKU() {
    const { fit, size, inseam } = this.#currentSelection
    const skuMapping = this.#variantOptions.sku_mapping
    
    if (!skuMapping || !skuMapping[fit]) {
      return null
    }
    
    const fitConfig = skuMapping[fit]
    let sku = fitConfig.sku_prefix
    
    // Add size to SKU
    if (size) {
      sku += `${size.replace('"', '')}`
    }
    
    // Handle inseam logic based on fit type
    if (fitConfig.has_inseam === true && inseam) {
      // OG Fit: Always include inseam (no dash between size and inseam)
      sku += `${inseam.replace('"', '')}`
    } else if (fitConfig.has_inseam === "conditional" && inseam) {
      // Sharp Fit: Only include inseam for 32" variants (no dash between size and inseam)
      if (fitConfig.inseam_32_only && inseam === '32"') {
        sku += `${inseam.replace('"', '')}`
      }
      // For 30" inseam, don't add to SKU (Sharp Fit 30" has no inseam in SKU)
    }
    
    return sku
  }

  /**
   * Calculates price based on current selection
   * @returns {number}
   */
  #calculatePrice() {
    // Try to find matching existing variant first
    const matchingVariant = this.#findMatchingVariant()
    if (matchingVariant) {
      return matchingVariant.price
    }
    
    // Fallback to base price from product or metaobject
    const basePrice = parseInt(this.dataset.basePrice || '0')
    
    // Add pricing logic based on selection if needed
    // This could be enhanced with metafield pricing data
    return basePrice
  }

  /**
   * Finds matching existing variant based on current selection
   * @returns {Object|null}
   */
  #findMatchingVariant() {
    const { fabric, fit, inseam, color } = this.#currentSelection
    
    return this.#existingVariants.find(variant => {
      const options = variant.options || []
      return (
        (!fabric || options.includes(fabric)) &&
        (!fit || options.includes(fit)) &&
        (!inseam || options.includes(inseam)) &&
        (!color || options.includes(color))
      )
    })
  }

  /**
   * Gets featured media for current selection
   * @returns {Object|null}
   */
  #getFeaturedMedia() {
    // This could be enhanced to return specific media based on selection
    return null
  }

  /**
   * Gets current selection
   * @returns {Object}
   */
  get currentSelection() {
    return { ...this.#currentSelection }
  }

  /**
   * Gets available options for current selection
   * @returns {Object}
   */
  get availableOptions() {
    return { ...this.#availableOptions }
  }

  /**
   * Checks if current selection is valid
   * @returns {boolean}
   */
  get isValidSelection() {
    return this.#isValidCombination()
  }
}

if (!customElements.get('custom-variant-picker')) {
  customElements.define('custom-variant-picker', CustomVariantPicker)
}
